<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'worksheet',
    path: '/worksheets/:id',
    component: 'Worksheet',
    params: [
      {
        name: 'id',
        prop: 'oid',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Worksheet',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
    },
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'full-screen',
  }));

  const $data = computed(() => ({
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = async () => {
    return props.oid === 'create' || props.oid == null
      ? $fn.value.fetch('get_worksheet_template', { asResource: true })
      : $fn.value.fetch('read_worksheet', {
          parameters: { id: props.oid },
          asResource: true,
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { item: $$load_item },
    ux: {},
    dataRefs: {
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isNew = computed(() => {
    if (loaded.value) {
      return item.value.isNew;
    }
    return null;
  });
  const isReadonly = computed(() => {
    return false;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [item.value.snapshot('number'), $fn.value.gettext('Worksheet')],
        icon: $config.value.worksheet.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_nan1s2mur_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_reloadSlickgrid_0057tx9e7_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'worksheets',
    };
  };
  const act_1040167445267876_reloadSlickgrid_0eqfi62i7_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'worksheets',
    };
  };
  const inst_evh_1040167445267876_success_adp7tadbh_getWhen = async (
    $event
  ) => {
    return item.value.id;
  };
  const act_1040167445267876_closeComponent_14gphvkxm_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const inst_evh_1040167445267876_success_nk64dne06_getWhen = async (
    $event
  ) => {
    return !item.value.id;
  };
  const act_1040167445267876_navigate_eps18waav_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.worksheet',
        params: {
          id: $event.data.id,
        },
      },
    };
  };
  const inst_evh_1040167445267876_success_5zrsv212i_getWhen = async (
    $event
  ) => {
    return !item.value.id;
  };
  const act_1040167445267876_setData_khzaa1w6i_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_1040167445267876_success_85yweazfm_getWhen = async (
    $event
  ) => {
    return item.value.id && !!$event.data && !!$event.data.id;
  };
  const act_1040167445267876_form_1z2bcnt11_getActionArgs = async ($event) => {
    return {
      name: props.formId,
      reset: true,
    };
  };
  const inst_evh_1040167445267876_success_r13ohu3j7_getWhen = async (
    $event
  ) => {
    return item.value.id && !!$event.data && !!$event.data.id;
  };
  const act_1040167445267876_crud_m3fb7t0c6_getActionArgs = async ($event) => {
    return {
      objectType: 'worksheet',
      op: 'upsert',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_nan1s2mur =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_nan1s2mur_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ju1ijevp4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_nan1s2mur,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ju1ijevp4],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_w02lp5ap5 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_6jwm19mks =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_w02lp5ap5,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_6jwm19mks],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_3j5al0snf =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_ddx75j1gh =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_3j5al0snf,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_ddx75j1gh],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_qvsekcs8h =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_8jhcy8t1r =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_qvsekcs8h,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_8jhcy8t1r],
    event: 'reload',
  });
  const act_6466705138206980_closeModal_lan61xv9k =
    new actionTypes.CloseModalAction({});
  const inst_evh_6466705138206980_deleted_1uuk85gcs =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeModal_lan61xv9k,
      event: 'deleted',
      displayName: 'closeModal',
    });
  const act_6466705138206980_reloadSlickgrid_0057tx9e7 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_6466705138206980_reloadSlickgrid_0057tx9e7_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_k7563qa5z =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadSlickgrid_0057tx9e7,
      event: 'deleted',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_1uuk85gcs,
      inst_evh_6466705138206980_deleted_k7563qa5z,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_reloadSlickgrid_0eqfi62i7 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_0eqfi62i7_getActionArgs,
    });
  const inst_evh_1040167445267876_success_adp7tadbh =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_0eqfi62i7,
      event: 'success',
      when: inst_evh_1040167445267876_success_adp7tadbh_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_closeComponent_14gphvkxm =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_14gphvkxm_getActionArgs,
    });
  const inst_evh_1040167445267876_success_nk64dne06 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_14gphvkxm,
      event: 'success',
      when: inst_evh_1040167445267876_success_nk64dne06_getWhen,
      displayName: 'closeComponent',
    });
  const act_1040167445267876_navigate_eps18waav =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_eps18waav_getActionArgs,
    });
  const inst_evh_1040167445267876_success_5zrsv212i =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_eps18waav,
      event: 'success',
      when: inst_evh_1040167445267876_success_5zrsv212i_getWhen,
      displayName: 'navigate',
    });
  const act_1040167445267876_setData_khzaa1w6i = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_khzaa1w6i_getActionArgs,
  });
  const inst_evh_1040167445267876_success_85yweazfm =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_khzaa1w6i,
      event: 'success',
      when: inst_evh_1040167445267876_success_85yweazfm_getWhen,
      displayName: 'setData',
    });
  const act_1040167445267876_form_1z2bcnt11 = new actionTypes.FormAction({
    actionArgs: act_1040167445267876_form_1z2bcnt11_getActionArgs,
  });
  const inst_evh_1040167445267876_success_r13ohu3j7 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_form_1z2bcnt11,
      event: 'success',
      when: inst_evh_1040167445267876_success_r13ohu3j7_getWhen,
      displayName: 'form',
    });
  const act_1040167445267876_crud_m3fb7t0c6 = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_m3fb7t0c6_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_success_adp7tadbh,
      inst_evh_1040167445267876_success_nk64dne06,
      inst_evh_1040167445267876_success_5zrsv212i,
      inst_evh_1040167445267876_success_85yweazfm,
      inst_evh_1040167445267876_success_r13ohu3j7,
    ],
  });
  const inst_evh_1040167445267876_submit_141ifqdor =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_m3fb7t0c6,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_141ifqdor],
    event: 'submit',
  });
  const act_1468474219929533_reloadComponentData_hqloa5z5c =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1468474219929533_reload_esnoj4w5r =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_reloadComponentData_hqloa5z5c,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_1468474219929533_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1468474219929533_reload_esnoj4w5r],
    event: 'reload',
  });
  const act_1306227577716690_reloadComponentData_igoe438cn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1306227577716690_reload_b9bnxg4ht =
    new actionTypes.EventHandlerImpl({
      action: act_1306227577716690_reloadComponentData_igoe438cn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_1306227577716690_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1306227577716690_reload_b9bnxg4ht],
    event: 'reload',
  });
  const act_8723442106880637_reloadComponentData_p7yx1fr0a =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8723442106880637_reload_3ysehb0on =
    new actionTypes.EventHandlerImpl({
      action: act_8723442106880637_reloadComponentData_p7yx1fr0a,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_8723442106880637_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8723442106880637_reload_3ysehb0on],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_ju1ijevp4,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_6jwm19mks,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_ddx75j1gh,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_8jhcy8t1r,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_1uuk85gcs,
    inst_evh_6466705138206980_deleted_k7563qa5z,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_success_adp7tadbh,
    inst_evh_1040167445267876_success_nk64dne06,
    inst_evh_1040167445267876_success_5zrsv212i,
    inst_evh_1040167445267876_success_85yweazfm,
    inst_evh_1040167445267876_success_r13ohu3j7,
    inst_evh_1040167445267876_submit_141ifqdor,
    save: inst_evh_1040167445267876_submit_141ifqdor,
    inst_evh_1040167445267876_submit,
    inst_evh_1468474219929533_reload_esnoj4w5r,
    inst_evh_1468474219929533_reload,
    inst_evh_1306227577716690_reload_b9bnxg4ht,
    inst_evh_1306227577716690_reload,
    inst_evh_8723442106880637_reload_3ysehb0on,
    inst_evh_8723442106880637_reload,
  };

  const evh_7315092382398562_close_ju1ijevp4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ju1ijevp4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_6jwm19mks = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_6jwm19mks.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_ddx75j1gh = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_ddx75j1gh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_8jhcy8t1r = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_8jhcy8t1r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_1uuk85gcs = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_1uuk85gcs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_k7563qa5z = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_k7563qa5z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_adp7tadbh = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_adp7tadbh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_nk64dne06 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_nk64dne06.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_5zrsv212i = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_5zrsv212i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_85yweazfm = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_85yweazfm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_r13ohu3j7 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_r13ohu3j7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_141ifqdor = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_141ifqdor.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_reload_esnoj4w5r = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_reload_esnoj4w5r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_reload = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1306227577716690_reload_b9bnxg4ht = async (event, scope) => {
    return await actions.inst_evh_1306227577716690_reload_b9bnxg4ht.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1306227577716690_reload = async (event, scope) => {
    return await actions.inst_evh_1306227577716690_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8723442106880637_reload_3ysehb0on = async (event, scope) => {
    return await actions.inst_evh_8723442106880637_reload_3ysehb0on.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8723442106880637_reload = async (event, scope) => {
    return await actions.inst_evh_8723442106880637_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    card
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Worksheet"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.worksheet.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcWorksheetHeader
          :worksheet="item"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcWorksheetHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="worksheet"
        autofocus="first"
        v-if="isNew"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <UcWorksheetEditorFieldset :worksheet="item">
          </UcWorksheetEditorFieldset>
        </ItpCol>
      </ItpForm>
      <ItpPanel
        collapsible
        :caption="pgettext('Worksheet', 'Worksheet data')"
        carousel
        v-if="!isNew"
      >
        <UcWorksheetData
          :worksheet="item"
          :formId="formId"
          :reload="lastLoaded"
          class="pt-3"
          @reload="evh_1468474219929533_reload($event, {})"
        >
        </UcWorksheetData>
      </ItpPanel>
      <hr v-if="!isNew" />
      <div class="overflow-hidden w-100" v-if="!isNew">
        <UcWorksheetWorkItems
          :worksheet="item"
          :isReadonly="isReadonly"
          :reload="lastLoaded"
          class="h-100 w-100"
          @reload="evh_1306227577716690_reload($event, {})"
        >
        </UcWorksheetWorkItems>
      </div>
      <hr v-if="!isNew" />
      <div class="overflow-hidden w-100" v-if="!isNew">
        <UcWorksheetInvoiceLineAccountings
          :worksheet="item"
          :isReadonly="isReadonly"
          :reload="lastLoaded"
          class="h-100 w-100"
          @reload="evh_8723442106880637_reload($event, {})"
        >
        </UcWorksheetInvoiceLineAccountings>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.payment.color"
        v-if="loaded"
      >
        <ItpButton
          :text="pgettext('Button', 'Create worksheet')"
          type="submit"
          :form="forms.worksheet"
          variant="primary"
          :spinning="actions.save.isRunning"
          class="me-2"
          v-if="isNew"
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
